import { ButtonContainer, DEButton } from "@repo/dynamicEditor/src/buttons";
import DynamicEditorContainer from "@repo/dynamicEditor/src/container";
import { usePendingEdits } from "@repo/dynamicEditor/src/contexts/pendingEdits";
import {
	traverseProperty,
	traversePropertyUpdate,
	traversePropertyValue,
} from "@repo/dynamicEditor/src/helpers/dynamicProperties";
import { useMappedSections } from "@repo/dynamicEditor/src/helpers/mappedSectionHook";
import { ComponentValidationResult } from "@repo/dynamicEditor/src/models/mappedComponent";
import { WebViewContainer } from "@repo/dynamicEditor/src/webView";
import { clone, downloadFile, log, MultiValidatorFn, styles } from "@repo/helpers";
import { keyPair, KeyPairArray, SerializedFile } from "@repo/models";
import { ButtonTypes, Row } from "@repo/ui";
import { MutableRefObject, PropsWithChildren, useCallback, useEffect, useRef, useState } from "react";

import { getPortalComponents } from "../components";
import { MappedComponent, section } from "../components/portalDynamicComponent";
import { mapSection } from "../sectionMapping";
import { ITemplateData } from "../templateDataHook";
import style from "./style.module.scss";

//Consider the possibility of stepping away from templateData being a ref
//If doing await thing, need to consider the possibility that value hasn't changed
//If doing full queue, can roll back updateProperties to just multiple updateProperty calls

function DynamicTab<TData extends ITemplateData>(props: DTProps<TData>) {
	const { templateData, sections, dirty, submitButtonText, saveAndEmail, loaded } = props;
	const { updateData, editData, initialized } = props;
	const { validator, updateValues, save, postSave } = props;
	const [saving, setSaving] = useState(false);
	const getMappedSections = useCallback(
		function () {
			if (!sections) return [];
			return sections.map(mapSection({ ...props, ...templateData.current }));
		},
		[sections, dirty, loaded],
	);

	const { mappedSections, validationResults, completed } = useMappedSections(getMappedSections, [dirty], validator);

	const { updateProperty, updateProperties, discardEdits } = useUpdateQueue(
		initialized,
		templateData,
		updateValues,
		updateData,
		editData,
	);
	const pendingEdits = usePendingEdits();
	const { cancel } = props;
	/**
	 * Cancel reverts the item to its saved state. First drop every edit that has not been written yet: typed
	 * within a field's 250ms debounce, queued, or waiting on async business logic (e.g. the zip lookup).
	 * Otherwise it lands after the revert: that field comes back, or for a running edit every earlier edit too.
	 */
	const onCancel = useCallback(() => {
		pendingEdits.discard();
		discardEdits();
		if (cancel) cancel();
	}, [pendingEdits, discardEdits, cancel]);

	const onSave = useCallback(
		async function (email: boolean) {
			try {
				setSaving(true);
				await save(email);
			} catch (ex) {
				log(`Error Saving: ${ex}`, "error");
			} finally {
				setSaving(false);
				if (postSave) await postSave();
			}
		},
		[setSaving, save, postSave],
	);

	const getFile = useCallback(function (fileId: number) {
		downloadFile("/MVC/AgentPortal/GetRepDocument/" + fileId);
	}, []);

	const deleteFile = useCallback(
		async function (fileId: number, propId: string) {
			const files = traverseProperty(propId, templateData.current) as SerializedFile[];
			if (!files) return;
			const fileIndex = files.findIndex((f) => f.id === fileId);
			if (fileIndex === -1) return;
			const newFiles = [...files];
			const file = clone(files[fileIndex]);
			newFiles.splice(fileIndex, 1, file);
			file.deleted = !file.deleted;
			await updateProperty(propId, newFiles);
			//TODO
		},
		[updateProperty],
	);

	const valueChanged = useCallback(
		async function (value: any, id: string, parentPropertyName?: string, dontEdit = false) {
			props.keepAlive && props.keepAlive();
			await updateProperty(id, value, parentPropertyName, dontEdit);
		},
		[props.keepAlive, updateProperty],
	);

	return (
		<Row id={props.name} tag="section" className={style.container}>
			{props.children}
			<DynamicEditorContainer
				templateData={templateData.current}
				validationResults={validationResults}
				sections={mappedSections}
				validateOnSave
			>
				<WebViewContainer
					containerClass={styles(props.containerClass /** style.middleContainers */)}
					getCustomComponent={getPortalComponents({
						...props,
						...templateData.current,
					})}
					completed={completed}
					addItem={emptyItemFn}
					removeItem={emptyItemFn}
					valueChanged={valueChanged}
					deleteUploadedFile={deleteFile}
					getUploadedFile={getFile}
					valuesChanged={updateProperties}
					readOnly={props.entryDisabled || saving}
					customFunctions={props.customFunctions}
					pendingEdits={pendingEdits}
				>
					<ButtonContainer completed={completed} submitting={saving} submitError={props.submissionError}>
						<DEButton onClick={() => onSave(false)} types={[ButtonTypes.Primary]} mustBeComplete>
							{submitButtonText ?? "Save"}
						</DEButton>
						{saveAndEmail && (
							<DEButton onClick={() => onSave(true)} types={[ButtonTypes.Green]} mustBeComplete>
								{"Save and Email"}
							</DEButton>
						)}
						{props.cancel && <DEButton onClick={onCancel}>Cancel</DEButton>}
					</ButtonContainer>
				</WebViewContainer>
			</DynamicEditorContainer>
		</Row>
	);
}

function emptyItemFn() {
	return () => null;
}

function useUpdateQueue<TData>(
	initialized: boolean,
	data: MutableRefObject<TData>,
	changeFn: (id: string, value: any, newState: TData, parentComponent?: string) => Promise<EditResult<TData>>,
	updateFn: (s: TData) => void,
	editFn: (s: TData) => void,
) {
	// One queue per editor (was a single module-level array shared by every mounted DynamicTab, so two editors
	// could run, overwrite (same id) or drop each other's requests). discardEdits relies on this.
	const updateQueue = useRef<KeyPairArray<EditRequest>>(null);
	updateQueue.current ??= new KeyPairArray<EditRequest>();
	const queueRecord = useRef([] as EditRequestRecord[]);
	const runningQueue = useRef(false);
	const runningId = useRef<number>(null);
	const lastQueueId = useRef(0);
	/** Bumped by discardEdits; an edit that started under an older generation never writes its result */
	const generation = useRef(0);

	const [dirtyQueue, setDirtyQueue] = useState(0);
	useEffect(() => {
		if (runningQueue.current) return;
		const nextInQueue = updateQueue.current.data[0]?.id as number;
		if (nextInQueue) executeEdit(nextInQueue);
		else lastQueueId.current = 0;
	}, [dirtyQueue]);

	function runQueue() {
		if (runningQueue.current) return;
		if (!updateQueue.current.any()) return;
		setDirtyQueue(Math.random());
	}

	async function executeEdit(id: number) {
		runningQueue.current = true;
		const editRequest = updateQueue.current.getValue(id);
		if (!editRequest) {
			runningQueue.current = false;
			return;
		}

		runningId.current = id;
		try {
			await pushUpdate(editRequest);
		} catch (ex) {
			// The business logic failed (e.g. a request it makes), so this edit is not applied. Carry on with the
			// next one: the queue used to stay "running" for good, so no later edit was applied (and Cancel could
			// not clear it) until the page was reloaded, and every caller waiting on an edit waited forever.
			log(ex, "error");
		}
		editRequest.resolve();
		updateQueue.current.removeId(id);
		runningId.current = null;
		runningQueue.current = false;
		runQueue();

		async function pushUpdate(editRequest: EditRequest) {
			//If dev, log record for tracking
			if (import.meta.env.DEV) queueRecord.current.push({ executeTime: new Date(), ...editRequest });

			const { properties, parentComponent, dontEdit } = editRequest;
			if (!initialized) return;
			const startedIn = generation.current;
			let changed = false;
			//Shallow clone (just one level down) template data
			let newState = {} as TData;
			const keys = Object.keys(data.current);
			for (const key of keys) newState[key] = { ...data.current[key] };

			// Being one level deep, the copy shares the objects below that with data.current, so a nested property
			// (a.b.c) is set in data.current itself as soon as its change runs. Keep what the properties hold now: if
			// the business logic fails they are put back, otherwise the next edit would apply the failed one.
			const current = data.current;
			const before = properties.map((property) => traversePropertyValue(property.id as string, current));
			try {
				for (const property of properties) {
					const result = await changeFn(property.id as string, property.value, newState, parentComponent);
					// Discarded (Cancel) while business logic ran: newState holds every pre-Cancel edit, so never write it
					if (startedIn !== generation.current) return;
					changed = changed || result.changed;
					newState = result.newState;
				}
			} catch (ex) {
				properties.forEach((property, i) => {
					const id = property.id as string;
					if (traversePropertyValue(id, current) !== before[i])
						traversePropertyUpdate(id, current, before[i]);
				});
				throw ex;
			}
			data.current = newState;
			if (changed && !dontEdit) editFn(newState);
			else updateFn(newState);
		}
	}

	const updateProperties = useCallback(function (properties: keyPair[], parentComponent?: string, dontEdit = false) {
		const nextId = ++lastQueueId.current;
		const editRequest = {
			properties,
			parentComponent,
			dontEdit,
		} as EditRequest;
		const promise = new Promise<void>((resolve) => {
			editRequest.resolve = resolve;
		});
		updateQueue.current.setValue(nextId, editRequest);
		runQueue();
		return promise;
	}, []);

	const updateProperty = useCallback(
		function (id: string, value: any, parentComponent?: string, dontEdit = false) {
			return updateProperties([{ id, value }], parentComponent, dontEdit);
		},
		[updateProperties],
	);

	/**
	 * Drops every edit sent before this call: queued ones are removed (their promises resolve, so no caller
	 * hangs) and the running one finishes without writing. Edits sent afterwards run normally.
	 */
	const discardEdits = useCallback(function () {
		generation.current++;
		// Iterate a copy: removeId splices the queue's array
		for (const { id, value: request } of updateQueue.current.data.slice()) {
			if (id === runningId.current) continue;
			updateQueue.current.removeId(id);
			request.resolve?.();
		}
	}, []);

	return { updateProperty, updateProperties, discardEdits };
}

export default DynamicTab;
interface EditRequest {
	properties: keyPair[];
	parentComponent?: string;
	dontEdit: boolean;
	resolve?: () => void;
}
interface EditRequestRecord extends EditRequest {
	executeTime: Date;
}
interface EditResult<TData> {
	changed: boolean;
	newState: TData;
}
export interface DTProps<TData extends ITemplateData> extends PropsWithChildren<any> {
	name: string;
	save: (email: boolean) => Promise<any>;
	postSave?: () => Promise<any>;
	cancel?: () => void;
	dirty: number;
	containerClass?: string;
	entryDisabled?: boolean;
	keepAlive?: () => void;
	submitButtonText?: string;
	saveAndEmail?: boolean;

	validator: (component: MappedComponent) => MultiValidatorFn<any, ComponentValidationResult>;
	initialized: boolean;
	sections: section[];
	templateData: MutableRefObject<TData>;
	updateValues?: (id: string, value: any, newState: TData, parentComponent?: string) => Promise<EditResult<TData>>;
	editData: (data: TData) => void;
	updateData: (data: TData) => void;
	customFunctions?: (name: string) => () => Promise<void>;
}
